import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import withStyles from '../../../hoc/withStyles';
import styles from '../styles/PriceRangeSlider.style';

const PriceRangeSlider = ({
  min,
  max,
  minRange,
  onChange,
  onDragEnd,
  className,
  step,
  initialTrackWidth,
  appliedPriceRange,
  priceFlag,
  setPriceFlag,
  checkAppliedRange,
  setCheckAppliedRange,
  setTabPriceRange,
  setPriceFilterFlagActn,
  hideTooltip,
  redesign,
  dynamicMin,
  dynamicMax,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const thumbWidth = 28;
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const minValRef = useRef(null);
  const maxValRef = useRef(null);
  const range = useRef(null);
  const track = useRef(null);
  const minValToolTip = useRef(null);
  const maxValToolTip = useRef(null);
  const [minToolTip, setMinToolTip] = useState(() => {
    return dynamicMin !== null ? dynamicMin : 0;
  });
  const [maxToolTip, setMaxToolTip] = useState(() => {
    return dynamicMax !== null ? dynamicMax : 0;
  });
  const prevInitialTrackWidth = useRef({ initialTrackWidth }).current;
  // Convert to percentage
  const getPercent = useCallback(
    (value) => {
      return min > value ? 0 : Math.round(((value - min) / (max - min)) * 100);
    },
    [min, max]
  );
  const lowerValUpdate = (event, dynamicVal) => {
    const val = event ? event.target.value : dynamicVal;
    const value = Math.min(+val, maxVal - minRange);
    const trackWidth = track?.current?.clientWidth;
    const pos = ((value - min) / (max - min)) * (trackWidth - thumbWidth);
    setMinToolTip(pos);
    setMinVal(value);
  };

  const higherValUpdate = (event, dynamicVal) => {
    const val = event ? event.target.value : dynamicVal;
    const value = Math.max(+val, minVal + minRange);
    const trackWidth = track?.current?.clientWidth;
    const pos = ((value - max) / (max - min)) * (trackWidth - thumbWidth);
    setMaxToolTip(pos);
    setMaxVal(value);
  };

  const newWidthCls = (value) => (value?.toString()?.length > 4 ? 'new-width' : '');

  // Set width of the range to decrease from the left side
  useEffect(() => {
    const current = maxValRef?.current;
    const rCurrent = range?.current;
    const rCurrentStyle = rCurrent?.style;
    const tCurrentStyle = track?.current?.style;
    const minCurrentStyle = minValToolTip?.current?.style;
    if (current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(+current.value);

      if (rCurrent) {
        rCurrentStyle.left = `${minPercent}%`;
        rCurrentStyle.width = `${maxPercent - minPercent}%`;
      }
      if (tCurrentStyle)
        tCurrentStyle.background = `linear-gradient(to right, #ced4da ${minPercent}%, transparent 0, transparent ${maxPercent}%,#ced4da 0 ) no-repeat 0 100% / 100% 100%`;
    }
    if (minCurrentStyle) minCurrentStyle.transform = `translateX(${minToolTip}px)`;
  }, [minVal, getPercent]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    const current = minValRef?.current;
    const rCurrent = range?.current;
    const rCurrentStyle = rCurrent?.style;
    const tCurrentStyle = track?.current?.style;
    const maxCurrentStyle = maxValToolTip?.current?.style;
    if (current) {
      const minPercent = getPercent(+current.value);
      const maxPercent = getPercent(maxVal);

      if (rCurrent) {
        rCurrentStyle.width = `${maxPercent - minPercent}%`;
      }
      if (tCurrentStyle)
        tCurrentStyle.background = `linear-gradient(to right, #ced4da ${minPercent}%, transparent 0, transparent ${maxPercent}%,#ced4da 0) no-repeat 0 100% / 100% 100%`;
    }
    if (maxCurrentStyle)
      maxCurrentStyle.transform = `translateX(${maxToolTip + initialTrackWidth - thumbWidth}px)`;
  }, [maxVal, getPercent]);

  // Get min and max values when their state changes
  useEffect(() => {
    onChange({ min: minVal, max: maxVal });
  }, [minVal, maxVal, onChange]);

  useEffect(() => {
    setMinVal(min);
    setMaxVal(max);
  }, [min, max]);

  useEffect(() => {
    let arr = ['', '', ''];
    if (
      checkAppliedRange &&
      appliedPriceRange &&
      appliedPriceRange?.displayName !== '' &&
      appliedPriceRange?.displayName
    ) {
      arr = appliedPriceRange?.displayName?.split(' ');
      setMinVal(parseFloat(arr[0]));
      setMaxVal(parseFloat(arr[2]));
    } else {
      setMinVal(min);
      setMaxVal(max);
      setCheckAppliedRange(false);
    }
  }, [min, max]);

  useEffect(() => {
    const minPos = ((minVal - min) / (max - min)) * (initialTrackWidth - thumbWidth);
    setMinToolTip(minPos);
    const minCurrentStyle = minValToolTip?.current?.style;
    if (minCurrentStyle) minCurrentStyle.transform = `translateX(${minToolTip}px)`;
    const maxPos = ((maxVal - max) / (max - min)) * (initialTrackWidth - thumbWidth);
    setMaxToolTip(maxPos);
    const maxCurrentStyle = maxValToolTip?.current?.style;
    if (maxCurrentStyle)
      maxCurrentStyle.transform = `translateX(${maxToolTip + initialTrackWidth - thumbWidth}px)`;
  });

  useEffect(() => {
    if (prevInitialTrackWidth !== initialTrackWidth) {
      const maxCurrentStyle = maxValToolTip?.current?.style;
      if (maxCurrentStyle)
        maxCurrentStyle.transform = `translateX(${maxToolTip + initialTrackWidth - thumbWidth}px)`;
    }
  }, [initialTrackWidth]);

  useEffect(() => {
    if (priceFlag) {
      setMinVal(min);
      setMaxVal(max);
      setPriceFlag(false);
      setPriceFilterFlagActn(false);
      setTabPriceRange(false);
    }
  }, [priceFlag]);

  useEffect(() => {
    if (dynamicMin) {
      lowerValUpdate(null, dynamicMin);
    }
  }, [dynamicMin]);

  useEffect(() => {
    if (dynamicMax) {
      higherValUpdate(null, dynamicMax);
    }
  }, [dynamicMax]);

  return (
    <div className={className}>
      <div className={`pd-top ${redesign ? 'quick-filters-design' : ''}`}>
        <div className="range" data-locator="filter_price_range_left">
          <input
            type="range"
            min={min}
            max={max}
            value={minVal}
            ref={minValRef}
            aria-label="slider"
            aria-valuemax={max}
            aria-valuemin={min}
            aria-valuenow={minVal}
            onChange={lowerValUpdate}
            onMouseUp={onDragEnd}
            onTouchEnd={onDragEnd}
            onKeyUp={onDragEnd}
            step={step}
            className={classnames(
              'thumb thumb--zindex-3',
              {
                'thumb--zindex-5': minVal > max - 100,
              },
              { 'thumb-redesign': redesign }
            )}
          />

          <span
            className={`min-val-tooltip ${newWidthCls(minVal)} ${hideTooltip ? 'hide' : ''}`}
            ref={minValToolTip}
          >
            {`$${minVal}`}
          </span>
        </div>
        <div className="range" data-locator="filter_price_range_right">
          <input
            type="range"
            min={min}
            max={max}
            value={maxVal}
            aria-label="slider"
            aria-valuemax={max}
            aria-valuemin={min}
            aria-valuenow={maxVal}
            ref={maxValRef}
            onChange={higherValUpdate}
            onMouseUp={onDragEnd}
            onTouchEnd={onDragEnd}
            onKeyUp={onDragEnd}
            step={step}
            className={`thumb ${redesign ? 'thumb-redesign' : ''} thumb--zindex-4`}
          />
          <span
            className={`max-val-tooltip ${newWidthCls(maxVal)} ${hideTooltip ? 'hide' : ''}`}
            ref={maxValToolTip}
          >
            {`$${maxVal}`}
          </span>
        </div>

        <div className="slider">
          <div ref={track} className="slider__track" />
          <div ref={range} className="slider__range" />
          <div
            className={`slider__left-value ${redesign ? 'hide' : ''}`}
            data-locator="filter_price_left_limit"
          >
            {min}
          </div>
          <div
            className={`slider__right-value ${redesign ? 'hide' : ''}`}
            data-locator="filter_price_right_limit"
          >
            {max}
          </div>
        </div>
        {redesign && (
          <div className="price-redesign-wrapper">
            <span>${minVal}</span>
            {'-'}
            <span>${maxVal}</span>
          </div>
        )}
      </div>
    </div>
  );
};

PriceRangeSlider.defaultProps = {
  minRange: 1,
  className: '',
  step: '.01',
  initialTrackWidth: 0,
  appliedPriceRange: {},
  priceFlag: false,
  setPriceFlag: () => {},
  checkAppliedRange: false,
  setCheckAppliedRange: () => {},
  setTabPriceRange: () => {},
  setPriceFilterFlagActn: () => {},
};

PriceRangeSlider.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  minRange: PropTypes.number,
  initialTrackWidth: PropTypes.number,
  step: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  className: PropTypes.string,
  appliedPriceRange: PropTypes.shape({}),
  priceFlag: PropTypes.bool,
  setPriceFlag: PropTypes.func,
  checkAppliedRange: PropTypes.bool,
  setCheckAppliedRange: PropTypes.func,
  setTabPriceRange: PropTypes.func,
  setPriceFilterFlagActn: PropTypes.func,
};

export default withStyles(PriceRangeSlider, styles);
export { PriceRangeSlider as PriceRangeSliderVanilla };
